// Storefront URL-state search: instant client-side results swap.
//
// The search page (frontend/search/index.blade.php) holds all filter state in a
// GET form (#tc-filter-form) and renders results into #tc-search-results. Every
// control funnels through the form's submit event (onchange auto-submit, slider
// `set`, search enter), and pagination links carry data-tc-page. This controller
// intercepts both, fetches the cached /api/trips/results fragment, and swaps it
// in — replacing the full-page reload with a ~50-255ms swap. Adjacent pages are
// prefetched so paging feels instant.

import { hidePassedDepartures } from "@/search/departureFilter";

(function () {
    "use strict";

    // Result-card "+N more" toggles. Defined here, not in the fragment: a <script>
    // injected via innerHTML on a results swap never runs, so inline handlers break.
    function animateItems(container) {
        if (!container) {
            return;
        }
        container.querySelectorAll(".animate-item").forEach((item, index) => {
            item.classList.remove("visible");
            setTimeout(() => item.classList.add("visible"), index * 50);
        });
    }

    function toggleMeta(prefix, id, showMore) {
        const more = document.getElementById(prefix + "-show-more-" + id);
        const less = document.getElementById(prefix + "-show-less-" + id);
        if (!more || !less) {
            return;
        }
        more.style.display = showMore ? "block" : "none";
        less.style.display = showMore ? "none" : "block";
        setTimeout(() => animateItems(showMore ? more : less), 50);
    }

    window.animateItems = animateItems;
    window.showMoreActivities = (id) => toggleMeta("activities", id, true);
    window.showLessActivities = (id) => toggleMeta("activities", id, false);
    window.showMoreDestinations = (id) => toggleMeta("destinations", id, true);
    window.showLessDestinations = (id) => toggleMeta("destinations", id, false);
    window.showMoreCategories = (id) => toggleMeta("category", id, true);
    window.showLessCategories = (id) => toggleMeta("category", id, false);

    const FORM_ID = "tc-filter-form";
    const CONTAINER_ID = "tc-search-results";
    const ENDPOINT = "/api/trips/results";

    function init() {
        const form = document.getElementById(FORM_ID);
        const container = document.getElementById(CONTAINER_ID);
        if (!form || !container) {
            return;
        }

        hidePassedDepartures(container);

        // query string -> Promise<{html, pagination}>; doubles as the prefetch cache.
        const cache = new Map();

        // Pending debounce for live search-as-you-type (see the input listener below).
        let searchTimer;

        // Current filter state as serialized from the form (checked boxes, radios,
        // selects, search text, and the sliders' hidden range inputs).
        function formParams() {
            const params = new URLSearchParams();
            new FormData(form).forEach((value, key) => params.append(key, value));
            return params;
        }

        function fetchParams(page) {
            const params = formParams();
            params.set("page", page);
            params.set("basePath", window.location.pathname);
            return params.toString();
        }

        function fetchFragment(qs) {
            if (cache.has(qs)) {
                return cache.get(qs);
            }
            const promise = fetch(ENDPOINT + "?" + qs, { headers: { Accept: "application/json" } })
                .then((response) => {
                    // Non-2xx (e.g. 429 when rate-limited) must reject, not resolve to
                    // an error body — swapTo's catch then leaves the current results in place.
                    if (!response.ok) {
                        throw new Error("fragment request failed: " + response.status);
                    }
                    return response.json();
                })
                .catch((error) => {
                    cache.delete(qs);
                    throw error;
                });
            cache.set(qs, promise);
            return promise;
        }

        // Reflect the active filters + page in the address bar (drops empty values).
        function pushUrl(page) {
            const params = new URLSearchParams();
            for (const [key, value] of formParams()) {
                if (value !== "" && value !== null) {
                    params.append(key, value);
                }
            }
            params.delete("page");
            if (page > 1) {
                params.set("page", page);
            }
            const qs = params.toString();
            window.history.pushState({ tcPage: page }, "", window.location.pathname + (qs ? "?" + qs : ""));
        }

        function prefetchAround(page, pagination) {
            const last = pagination && pagination.last_page ? pagination.last_page : 1;
            [page + 1, page - 1].forEach((p) => {
                if (p >= 1 && p <= last) {
                    // Fire-and-forget: ignore failures (e.g. a rate-limited prefetch).
                    fetchFragment(fetchParams(p)).catch(() => {});
                }
            });
        }

        // Skeleton markup (4 pulsing card placeholders) the server rendered into a
        // hidden <template>. Matches the system's loading style.
        const skeletonHost = document.getElementById("tc-search-skeleton");
        const skeletonHtml = skeletonHost ? skeletonHost.innerHTML : "";

        async function swapTo(page, options) {
            const opts = options || {};
            container.setAttribute("aria-busy", "true");
            // Show the skeleton only once a fetch is slow enough to notice. Cached /
            // fast swaps (~50ms) resolve before this fires, so they never flash it;
            // slow responses get the familiar skeleton instead of a frozen page.
            const previousHtml = container.innerHTML;
            const loadingTimer = setTimeout(() => {
                if (skeletonHtml) {
                    container.innerHTML = skeletonHtml;
                }
            }, 180);

            let data;
            try {
                data = await fetchFragment(fetchParams(page));
            } catch (e) {
                clearTimeout(loadingTimer);
                container.innerHTML = previousHtml; // restore last-good results on failure
                container.removeAttribute("aria-busy");
                return;
            }

            clearTimeout(loadingTimer);
            container.innerHTML = data.html;
            hidePassedDepartures(container);
            container.removeAttribute("aria-busy");

            if (typeof window.handleContentChange === "function") {
                window.handleContentChange();
            }
            if (opts.push !== false) {
                pushUrl(page);
            }
            if (opts.scroll !== false) {
                const top = container.getBoundingClientRect().top + window.scrollY - 120;
                window.scrollTo({ top: top, behavior: "smooth" });
            }
            prefetchAround(data.pagination ? data.pagination.current_page : page, data.pagination);
        }

        // Filter / sort / search / slider changes all dispatch the form's submit.
        form.addEventListener("submit", (event) => {
            event.preventDefault();
            clearTimeout(searchTimer); // an explicit submit cancels any pending live-search
            cache.clear(); // filters changed: prior pages are stale
            swapTo(1, { scroll: false });
        });

        // Live search-as-you-type (parity with the Livewire page's 800ms debounce).
        // The Enter key and Search button keep working as explicit submits, and the
        // search still works without JS via the form's GET action.
        const searchInput = form.querySelector('[name="searchKey"]');
        if (searchInput) {
            searchInput.addEventListener("input", () => {
                clearTimeout(searchTimer);
                searchTimer = setTimeout(() => {
                    cache.clear();
                    swapTo(1, { scroll: false });
                }, 800);
            });
        }

        // Pagination clicks (delegated so it survives innerHTML swaps).
        container.addEventListener("click", (event) => {
            const link = event.target.closest("[data-tc-page]");
            if (!link || !container.contains(link)) {
                return;
            }
            const page = parseInt(link.getAttribute("data-tc-page"), 10);
            if (!page) {
                return;
            }
            event.preventDefault();
            swapTo(page);
        });

        // Prefetch a page on hover so the click is instant.
        container.addEventListener("mouseover", (event) => {
            const link = event.target.closest("[data-tc-page]");
            if (link) {
                const page = parseInt(link.getAttribute("data-tc-page"), 10);
                if (page) {
                    fetchFragment(fetchParams(page)).catch(() => {});
                }
            }
        });

        // Carousel lazy images: load a carousel's data-src images on first
        // interaction. Delegated on the container so it works for the SSR
        // carousels and every swapped-in page. Idempotent.
        function loadCarouselImages(wrapper) {
            if (!wrapper) {
                return;
            }
            wrapper.querySelectorAll("img.carousel-lazy[data-src]").forEach((img) => {
                img.src = img.dataset.src;
                img.removeAttribute("data-src");
                img.classList.remove("carousel-lazy");
            });
        }
        const loadFromWrapper = (event) => {
            const wrapper = event.target.closest(".slider-wrapper");
            if (wrapper) {
                loadCarouselImages(wrapper);
            }
        };
        container.addEventListener("mouseover", loadFromWrapper);
        container.addEventListener("touchstart", loadFromWrapper, { passive: true });

        // Back / forward: reload to the popped URL so the sidebar controls and
        // results both reflect that state (the form is server-rendered).
        window.addEventListener("popstate", () => {
            window.location.reload();
        });
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", init);
    } else {
        init();
    }
})();
